import { defineStore } from 'pinia';
import { addFolder, deleteFolder, getFolderTree, moveFolder, renameFolder } from '@/api/photo-space';
import { useFileListStore } from './file-list';

interface TreeItem {
  key: number
  value: number
  title: string
  label: string
  pid: number
  children: TreeItem[]
}

interface DataItem {
  key: number
  title: string
  pid: number
  children: TreeItem[]
}

interface FolderListState {
  treeData: TreeItem[]
  treeDateLoading: boolean
  dataList: DataItem[]
}

const modifyKey = (data: API.GetFolderTreeResponse): any => {
  return data.map((item: API.FolderTreeItem) => ({
    key: item.id,
    value: item.id,
    title: item.label,
    label: item.label,
    pid: item.p_id,
    children: modifyKey(item.children || []),
  }));
};

export const useFolderListStore = defineStore('photo-folder-list', {
  state: (): FolderListState => ({
    treeData: [],
    treeDateLoading: false,
    dataList: [],
  }),
  getters: {
    currentFolder(): DataItem | undefined {
      const { pId } = useFileListStore();
      return this.dataList.find((item: DataItem) => item.key === pId);
    },
  },
  actions: {
    async getTreeData(refresh?: boolean) {
      if (!refresh && this.treeData.length > 0) {
        return Promise.resolve(this.treeData);
      }
      return getFolderTree({ p_id: 0 }).then((tree) => {
        this.treeData = modifyKey(tree);
        // 缓存文件夹树
        this.saveTreeCache();
      });
    },

    // ===== 文件夹树缓存（1天有效） =====
    initTreeFromCache() {
      const cached = this.loadTreeCache();
      if (cached) {
        this.treeData = cached.treeData;
      }
    },
    saveTreeCache() {
      try {
        uni.setStorageSync('photo_tree_cache', JSON.stringify({
          treeData: this.treeData,
          timestamp: Date.now(),
        }));
      }
      catch {}
    },
    loadTreeCache(): { treeData: any[] } | null {
      try {
        const raw = uni.getStorageSync('photo_tree_cache');
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() - data.timestamp > 86400000) {
          uni.removeStorageSync('photo_tree_cache');
          return null;
        }
        return data;
      }
      catch { return null; }
    },
    generateList(data: any[] = []): void {
      for (let i = 0; i < data.length; i++) {
        const node = data[i];
        const key = node.key;
        this.dataList.push({ key, title: node.title, pid: node.pid, children: node.children });
        if (node.children) {
          this.generateList(node.children);
        }
      }
    },
    async addFolderFun(pid: number, name: string) {
      return addFolder({ p_id: pid, fol_name: name });
    },
    async renameFolderFun(id: number, name: string) {
      return renameFolder({ fol_name: name, fol_id: id });
    },
    async moveFolderFun(id: number, pid: number) {
      return moveFolder({ fol_id: id, p_id: pid });
    },
    async deleteFolderFun(id: number) {
      return deleteFolder({ fol_id: id });
    },
    getParentKey(key: number): number {
      function getParentKeyFunc(key: number, tree: TreeItem[] = []): number | undefined {
        let parentKey: any;
        for (let i = 0; i < tree.length; i++) {
          const node: TreeItem = tree[i];
          if (node.children) {
            if (node.children.some(item => item.key === key)) {
              parentKey = node.key;
            }
            else if (getParentKeyFunc(key, node.children)) {
              parentKey = getParentKeyFunc(key, node.children);
            }
          }
        }
        return parentKey;
      }
      return getParentKeyFunc(key, this.treeData) || 0;
    },
  },
});
